import { z } from 'zod';
import type { CustomerCategory, LicenseCode, SanityLicense } from '../../types/licenses';
import {
  askIfExistingCustomer,
  hasNoAddressSection,
  hasVenueAddressSection,
  isSportsLicense,
  LicenseTypes,
} from '../../types/licenses';

export const CUSTOMER_INFORMATION_CONTENT_PATH = 'licenses.steps.customerInformation';

// Schema for the license information step.
// Mirrors patterns from member/utils/schemas but simplified for the license flow.
// Base schema (maximal requirements); dynamic builder will relax fields per license/flow.
export const BaseCustomerInformationSchema = z.object({
  contactName: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.contact.name.validation.required` }),
  email: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.contact.email.validation.required` })
    .email({ message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.contact.email.validation.invalid` }),
  phone: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.contact.phone.validation.required` })
    // Apply 8-digit Danish local number validation only when prefix is +45.
    // Assumes stored format starts with "+45" (optionally followed by space) before the 8 digits.
    .refine(
      v => {
        if (!v) return false; // handled by min(1) but keep defensive
        const trimmed = v.trim();
        if (/^\+45/.test(trimmed)) {
          // Remove +45 and any spaces after it, then strip remaining spaces
          const rest = trimmed.replace(/^\+45\s*/, '').replace(/\s+/g, '');
          return /^\d{8}$/.test(rest);
        }
        // For non-Danish prefixes, skip 8-digit enforcement
        return true;
      },
      { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.contact.phone.validation.length` },
    ),
  companyCountry: z.enum(['DK', 'GL', 'FO']).default('DK'),
  address: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.address.validation.required` }),
  zip: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.zip.validation.required` })
    .refine(v => /^\d+$/.test(v), {
      message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.zip.validation.format`,
    }),
  city: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.city.validation.required` })
    .refine(v => !/\d/.test(v), {
      message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.city.validation.noNumbers`,
    }),
  // Venue address fields
  performanceAddressType: z.enum(['company', 'venue', 'none']).optional(),
  venue: z
    .object({
      number: z.string().optional(),
      name: z.string().optional(),
      venueName: z.string().optional(),
      address: z.string().optional(),
      postCode: z.string().optional(),
      city: z.string().optional(),
      county: z.string().optional(),
      countryRegionCode: z.enum(['DK', 'GL', 'FO']).optional(),
    })
    .optional(),
  cvr: z
    .string()
    .optional()
    .refine(v => !v || /^\d{8}$/.test(v), {
      message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.cvr.validation.format`,
    }),
  companyName: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.companyName.validation.required` }),
  invoiceEmail: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.invoiceEmail.validation.required` })
    .email({ message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.invoiceEmail.validation.invalid` }),
  customerCategory: z
    .string({
      required_error: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.customerCategory.validation.required`,
    })
    .min(1, {
      message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.customerCategory.validation.required`,
    }),
  isExistingCustomer: z.boolean().optional(),
  customerNumber: z.string().optional(),
  remarkHeading: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.heading.validation.required` }),
  remark: z
    .string()
    .min(1, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.remark.validation.required` })
    .max(1000, { message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.remark.validation.max` }),
});

export type CustomerInformation = z.infer<typeof BaseCustomerInformationSchema>;
// Rely on inference for defaultCustomerInformation instead of importing its type
// LicenseTypes no longer needed here since defaults moved into schema files

export const defaultCustomerInformation = {
  contactName: '',
  email: '',
  phone: '',
  companyCountry: 'DK' as const,
  address: '',
  zip: '',
  city: '',
  cvr: '',
  companyName: '',
  invoiceEmail: '',
  customerCategory: '',
  remarkHeading: '',
  remark: '',
  performanceAddressType: 'none' as const,
  venue: undefined,
  isExistingCustomer: undefined,
  customerNumber: '',
};

// Returns a schema adapted to the provided license:
// - companyCategory optional for OtherContactForm (hidden in UI)
// - address/zip/city optional for licenses with no address section (hidden in UI)
// - other fields use base rules
export function getCustomerInformationSchema(license: SanityLicense | null | undefined) {
  if (!license) return BaseCustomerInformationSchema; // fallback
  const base = BaseCustomerInformationSchema.shape;
  const licenseCode = license.licenseId;
  const isOtherContactForm = licenseCode === LicenseTypes.OtherContactForm;
  const noAddress = hasNoAddressSection(licenseCode);
  const dynamicShape = {
    ...base,
    customerCategory: isOtherContactForm ? z.string().optional() : base.customerCategory,
    remarkHeading: isOtherContactForm ? base.remarkHeading : z.string().optional().default(''),
    remark: isOtherContactForm ? base.remark : z.string().optional().default(''),
    address: noAddress ? z.string().optional().default('') : base.address,
    zip: noAddress ? z.string().optional().default('') : base.zip,
    city: noAddress ? z.string().optional().default('') : base.city,
  } as const;

  return z.object(dynamicShape).superRefine((data, ctx) => {
    const usesVenueListSelection = hasVenueAddressSection(licenseCode);
    const shouldValidateVenue = !noAddress && (data.performanceAddressType === 'venue' || usesVenueListSelection);

    if (shouldValidateVenue) {
      const v = data.venue ?? {};

      // When the UI uses venue list selection, require a selected venue.
      if (usesVenueListSelection && !data.venue?.number) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: isSportsLicense(licenseCode)
            ? 'licenses.components.input.locationInput.validation.sportsRequired'
            : 'licenses.components.input.locationInput.validation.required',
          path: ['venue'],
        });
      }

      // When the UI uses AddressInput (different address tab), validate address fields as required.
      // In this flow there is no venue.number.
      if (!usesVenueListSelection) {
        if (!v.address) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.address.validation.required`,
            path: ['venue', 'address'],
          });
        }

        if (!v.postCode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.zip.validation.required`,
            path: ['venue', 'postCode'],
          });
        }

        if (!v.city) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.city.validation.required`,
            path: ['venue', 'city'],
          });
        }

        if (!v.countryRegionCode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.country.validation.required`,
            path: ['venue', 'countryRegionCode'],
          });
        }
      }
    }

    if (askIfExistingCustomer(licenseCode) && data.isExistingCustomer == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.existingCustomer.validation.required`,
        path: ['isExistingCustomer'],
      });
    }

    if (askIfExistingCustomer(licenseCode) && data.isExistingCustomer === true) {
      if (!data.customerNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.customerNumber.validation.required`,
          path: ['customerNumber'],
        });
      }

      if (data.customerNumber && !/^\d{7}$/.test(data.customerNumber)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.customerNumber.validation.format`,
          path: ['customerNumber'],
        });
      }
    }

    if (licenseCode === LicenseTypes.OtherContactForm) {
      if (!data.remarkHeading?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.heading.validation.required`,
          path: ['remarkHeading'],
        });
      }

      if (!data.remark?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${CUSTOMER_INFORMATION_CONTENT_PATH}.form.sections.company.remark.validation.required`,
          path: ['remark'],
        });
      }
    }
  });
}

export const toBaseEmailCustomerInformation = (
  form: CustomerInformation,
  licenseCode: LicenseCode,
): Record<string, string> => {
  return {
    Kontaktperson: form.contactName,
    'Kontakt e-mail': form.email,
    Telefon: form.phone,
    Firmanavn: form.companyName,
    ...(form.cvr ? { CVR: form.cvr } : {}),
    'Faktura e-mail': form.invoiceEmail,
    ...(askIfExistingCustomer(licenseCode)
      ? { 'Er du eksisterende kunde hos Koda?': form.isExistingCustomer ? 'Ja' : 'Nej' }
      : {}),
    ...(askIfExistingCustomer(licenseCode) && form.isExistingCustomer ? { Kundenummer: form.customerNumber } : {}),
    ...(form.remarkHeading?.trim() ? { Overskrift: form.remarkHeading } : {}),
    ...(form.remark?.trim() ? { Beskrivelse: form.remark } : {}),
  };
};

export const toInquiryEmailCustomerInformation = (
  customerInformation: CustomerInformation,
  licenseCode: LicenseCode,
  customerCategories?: CustomerCategory[] | null,
): Record<string, string | Record<string, string>> => {
  const selectedCategory = customerInformation.customerCategory?.trim?.() || '';

  const formattedCustomerCategory = (() => {
    if (!selectedCategory) return null;
    if (selectedCategory.includes(' - ')) return selectedCategory;
    const match = (customerCategories ?? []).find(cat => String(cat.id) === selectedCategory);
    const id = match?.id ? String(match.id) : null;
    const name = match?.name?.trim() || null;
    if (id && name) return `${id} - ${name}`;
    return null;
  })();

  const baseInfo = {
    ...toBaseEmailCustomerInformation(customerInformation, licenseCode),
    ...(formattedCustomerCategory ? { Kategori: formattedCustomerCategory } : {}),
  };

  if (customerInformation.performanceAddressType === 'venue') {
    return {
      ...baseInfo,
      Spillested: {
        Navn: customerInformation.venue?.name || customerInformation.venue?.venueName || '',
        Adresse: customerInformation.venue?.address || '',
        Postnummer: customerInformation.venue?.postCode || '',
        By: customerInformation.venue?.city || '',
        Region: customerInformation.venue?.county || '',
        Land: customerInformation.venue?.countryRegionCode || '',
        Id: customerInformation.venue?.number || '',
      },
    };
  } else {
    return {
      ...baseInfo,
      Land: customerInformation.companyCountry,
      Adresse: customerInformation.address,
      Postnummer: customerInformation.zip,
      By: customerInformation.city,
    };
  }
};
